import { getMessaging } from 'firebase/messaging';
import { environment } from 'src/environments/environment';

import { Injectable } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { getToken, onMessage } from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { AuthService } from '@auth/services/auth/auth.service';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { ToastTypes } from '@shared/models/toast';
import { ToastService } from '@shared/services/toast/toast.service';
import { UserDeviceService } from '@user/services/user-device/user-device.service';

@Injectable({
	providedIn: 'root',
})
export class PushNotificationsService {
	private messaging = getMessaging();

	constructor(
		private authService: AuthService,
		private userDeviceService: UserDeviceService,
		private toastService: ToastService,
		private router: Router,
	) {}

	async init(): Promise<void> {
		if (Capacitor.isNativePlatform()) {
			await this.initMobilePushNotifications();
		} else {
			await this.initWebPushNotifications();
		}
	}

	private async initMobilePushNotifications(): Promise<void> {
		if (!Capacitor.isPluginAvailable('PushNotifications')) return;

		const permission = await PushNotifications.requestPermissions();
		if (permission.receive !== 'granted') return;

		await PushNotifications.register();

		PushNotifications.addListener('registration', async (token: Token) => {
			await this.saveDeviceToken(token.value);
		});

		PushNotifications.addListener('registrationError', (error) => {
			this.toastService.showToast({
				title: 'Notifications',
				message: 'Registration error: ' + JSON.stringify(error),
				type: ToastTypes.ERROR,
			});
		});

		PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
			console.log('Mobile push notification received', JSON.stringify(notification));
			this.toastService.showToast({
				title: notification?.title || 'Notification',
				message: notification?.body || '',
				type: ToastTypes.INFO,
			});
		});

		PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
			console.log('Mobile push action performed', JSON.stringify(notification));
			this.router.navigate([notification.notification?.data?.link || '/']);
		});
	}

	private async initWebPushNotifications(): Promise<void> {
		try {
			if (Notification.permission === 'default') {
				this.toastService.showToast({
					title: 'Notifications',
					message: 'Would you like to enable push notifications?',
					type: ToastTypes.INFO,
					yes: async () => {
						const permission = await Notification.requestPermission();
						if (permission === 'granted') {
							const token = await getToken(this.messaging, { vapidKey: environment.firebase.vapidKey });
							if (token) {
								await this.saveDeviceToken(token);
								this.toastService.showToast({
									title: 'Notifications',
									message: 'Web notifications enabled',
									type: ToastTypes.SUCCESS,
								});
							}
						} else if (permission === 'denied') {
							this.toastService.showToast({
								title: 'Notifications Blocked',
								message: 'You have blocked notifications. To enable them, update your browser settings.',
								type: ToastTypes.WARNING,
								yes: () => {
									window.open('https://support.google.com/chrome/answer/3220216', '_blank');
								},
								no: () => {
									console.log('User chose not to view notification settings.');
								},
							});
						} else {
							console.warn('Notifications permission not granted.');
						}
					},
					no: () => {
						console.log('User declined enabling notifications.');
					},
				});
			} else if (Notification.permission === 'granted') {
				const token = await getToken(this.messaging, { vapidKey: environment.firebase.vapidKey });
				if (token) {
					await this.saveDeviceToken(token);
				}
			} else if (Notification.permission === 'denied') {
				this.toastService.showToast({
					title: 'Notifications Blocked',
					message: 'You have blocked notifications. To enable them, update your browser settings.',
					type: ToastTypes.WARNING,
					yes: () => {
						window.open('https://support.google.com/chrome/answer/3220216', '_blank');
					},
					no: () => {
						console.log('User chose not to view notification settings.');
					},
				});
			}

			onMessage(this.messaging, (payload) => {
				this.toastService.showToast({
					title: payload.notification?.title || 'Notification',
					message: payload.notification?.body || '',
					type: ToastTypes.INFO,
					onClick: () => {
						if (payload.data?.link) {
							this.router.navigate([payload.data?.link || '/']);
						}
					},
				});
			});
		} catch (error) {
			console.error('Error getting web push token:', error);
			this.toastService.showToast({
				title: 'Notifications',
				message: 'Error setting up web notifications',
				type: ToastTypes.ERROR,
			});
		}
	}

	async saveDeviceToken(token: string): Promise<void> {
		const currentUser = this.authService.currentUser;
		if (!currentUser?.uid) return;

		const deviceInfo = await Device.getInfo();
		const deviceId = await Device.getId();
		const deviceToken = token;

		await this.userDeviceService.addOrUpdateDevice(currentUser.uid, deviceToken, {
			token,
			platform: deviceInfo.platform,
			deviceId: deviceId?.identifier,
			deviceModel: deviceInfo.model,
			deviceName: deviceInfo?.name || 'Unkown Device Name',
			createdAt: Timestamp.now(),
			updatedAt: Timestamp.now(),
		});
	}

	async removeDeviceToken(token: string): Promise<void> {
		const currentUser = this.authService.currentUser;
		if (!currentUser?.uid) return;

		const deviceId = token;
		await this.userDeviceService.removeDevice(currentUser.uid, deviceId);
	}
}
